<template>
  <Modal :name="$options.name"
         :is-loading="isLoading"
         width="720"
         @close="clearForm"
         title="Ellenőrzés időpont foglalás">
    <template #body>
      <div class="book-date-modal-body">
        <b-field label="Ellenőrzés Időpontja">
          <b-datetimepicker
            v-model="form.date"
            :datepicker="{ showWeekNumber: false, trapFocus: true }"
            :timepicker="{ hourFormat: '24', incrementMinutes: 15 }"
            horizontal-time-picker
            :datetime-formatter="formatDateTimeInput"
            :datetime-creator="createDefaultDateTime"
            placeholder="Válassz dátumot és időpontot"
            icon="calendar"
            :icon-right="form.date ? 'close-circle' : ''"
            icon-right-clickable
            @icon-right-click="() => form.date = null"
            @input="fetchMonthBookingLimit">
          </b-datetimepicker>
        </b-field>
        <div class="has-background-grey-lighter px-2 py-1">
          Az ellenőrzéshez <strong>{{ inspectionBookCount }}</strong> szabad időpont szükséges
          <div v-if="bookingLimit">
            Erre a hónapra (<span class="is-capitalized">{{ getMonthNameByNumber(bookingLimit.honap) }}</span>) még
            <strong :class="`has-text-${getInspectionBookingLimitColor(bookingLimit.db, bookingLimit.db - bookingLimit.available_inspection_count)}`">
              {{ bookingLimit.available_inspection_count }}
            </strong>
            szabad időpont van.
          </div>
        </div>
        <b-field label="IRSZ">
          <b-input v-model="form.zipCode"
                   @keyup.enter.native="isLoading ? null : save()"
                   ref="formZipCode"
                   required></b-input>
        </b-field>
        <b-field label="Város">
          <b-input v-model="form.city"
                   @keyup.enter.native="isLoading ? null : save()"
                   ref="formCity"
                   required></b-input>
        </b-field>
        <b-field label="Cím">
          <b-input v-model="form.address"
                   @keyup.enter.native="isLoading ? null : save()"
                   ref="formAddress"
                   required></b-input>
        </b-field>
      </div>
    </template>
    <template #footer>
      <b-button type="is-primary"
                @click="save">
        Időpont mentése
      </b-button>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/Modals/common/BasicModal'
import { isValid as isValidDate, format as formatDate, parseISO, setHours, setMinutes } from 'date-fns'
import { getMonthNameByNumber, getInspectionBookingLimitColor } from '@/utils/utils'

export default {
  name: 'InspectionBookDateModal',
  components: {
    Modal,
  },
  data () {
    return {
      isLoading: false,
      form: {
        zipCode: null,
        city: null,
        address: null,
        date: null,
      },
      bookingLimit: null,
      bookingLimitMonthKey: null,
    }
  },
  methods: {
    getMonthNameByNumber,
    getInspectionBookingLimitColor,
    formatDateTimeInput (date) {
      return formatDate(date, 'yyyy. MM. dd. HH:mm')
    },
    createDefaultDateTime (date) {
      // első dátumválasztáskor 00:00 helyett 08:00 legyen az alapértelmezett időpont
      return setMinutes(setHours(date, 8), 0)
    },
    clearForm () {
      this.form.zipCode = null
      this.form.city = null
      this.form.address = null
      this.form.date = null
      this.bookingLimit = null
      this.bookingLimitMonthKey = null
    },
    fillForm (data) {
      this.form.zipCode = data.zipCode || null
      this.form.city = data.city || null
      this.form.address = data.address || null
      const parsedDate = data.date ? parseISO(data.date) : null
      this.form.date = isValidDate(parsedDate) ? parsedDate : null
      this.fetchMonthBookingLimit()
    },
    async save () {
      this.isLoading = true
      const dateIsValid = isValidDate(this.form.date)
      const zipCodeIsValid = this.$refs.formZipCode.checkHtml5Validity()
      const cityIsValid = this.$refs.formCity.checkHtml5Validity()
      const addressIsValid = this.$refs.formAddress.checkHtml5Validity()
      if (dateIsValid && zipCodeIsValid && cityIsValid && addressIsValid) {
        const resp = await this.$store.dispatch('bookInspectionDate', {
          inspectionID: this.inspectionID,
          zipCode: this.form.zipCode,
          city: this.form.city,
          address: this.form.address,
          date: formatDate(this.form.date, 'yyyy-MM-dd HH:mm:ss'),
        })
        if (resp) {
          await this.$store.dispatch('closeModal', this.$options.name)
          this.clearForm()
        }
      }
      if (!dateIsValid) {
        await this.$store.dispatch('showMessage', { type: 'danger', msg: 'Az időpont mező kitöltése kötelező' })
      }
      this.isLoading = false
    },
    async fetchMonthBookingLimit () {
      if (!this.form.date) {
        return
      }
      // óra/perc választásakor is jön input event, csak hónapváltáskor kérjük le újra
      const monthKey = formatDate(this.form.date, 'yyyy-MM')
      if (monthKey === this.bookingLimitMonthKey) {
        return
      }
      this.bookingLimitMonthKey = monthKey
      const resp = await this.$store.dispatch('fetchInspectionBookingLimits', this.form.date)
      if (resp.length > 0) {
        this.bookingLimit = resp[0]
      }
    },
  },
  computed: {
    modalPayload () {
      return this.$store.state.modal.modalPayloadData[this.$options.name]
    },
    inspectionID () {
      return this.modalPayload?.inspectionID
    },
    inspectionBookCount () {
      return this.modalPayload?.inspectionBookCount
    },
  },
  watch: {
    modalPayload: function (newVal, prevVal) {
      if (newVal) {
        this.fillForm(newVal)
      }
    },
  },
}
</script>

<style scoped>
/* a lenyitott dátum/idő választó görgetés nélkül elférjen a modal body-ban */
.book-date-modal-body {
  min-height: 25rem;
}
</style>
